import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { auth } from '../../firebase';
import { useLanguage } from '../../contexts/LanguageContext';

const PromoCodeGenerator = () => {
  const { t } = useLanguage();
  const [code, setCode] = useState('');
  const [amountOff, setAmountOff] = useState('');
  const [percentOff, setPercentOff] = useState('');
  const [duration, setDuration] = useState('once');
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Translation keys for the promo code component
  const i18n = {
    code: t('code'),
    amountOff: t('amountOff'),
    percentOff: t('percentOff'),
    duration: t('duration'),
    once: t('once'),
    repeating: t('repeating'),
    forever: t('forever'),
    generatePromoCode: t('generatePromoCode'),
    generating: t('generating'),
    promoCodeHistory: t('promoCodeHistory'),
    ron: t('ron'),
    createdBy: t('createdBy'),
    noPromoCodes: t('noPromoCodes'),
    failedToLoad: t('failedToLoad'),
    loadingPromoCodes: t('loadingPromoCodes'),
    failedToGenerate: t('failedToGenerate'),
    promoCodeGenerated: t('promoCodeGenerated')
  };

  // Fetch promo code history from placeholder API
  const fetchPromoHistory = useCallback(async () => {
    try {
      setLoading(true);
      const token = await auth.currentUser?.getIdToken();
      
      // Placeholder request - replace with your actual promo codes API endpoint
      const response = await axios.get('https://jsonplaceholder.typicode.com/posts', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      // Transform placeholder data to simulate promo code data
      const simulatedPromoData = response.data.slice(0, 10).map((item, index) => ({
        id: item.id,
        code: `PROMO${String(index + 1).padStart(3, '0')}`,
        amount_off: index % 3 === 0 ? Math.floor(Math.random() * 50) + 10 : null,
        percent_off: index % 3 === 1 ? Math.floor(Math.random() * 30) + 5 : null,
        duration: ['once', 'repeating', 'forever'][index % 3],
        created: new Date(Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000),
        createdBy: ['admin@servicegest.ro', 'manager@servicegest.ro', 'support@servicegest.ro'][index % 3]
      }));

      // Sort by created date (most recent first)
      setHistory(simulatedPromoData.sort((a, b) => new Date(b.created) - new Date(a.created)));
      setError('');
    } catch (error) {
      console.error('Error fetching promo history:', error);
      setError(i18n.failedToLoad);
    } finally {
      setLoading(false);
    }
  }, [i18n.failedToLoad]);

  useEffect(() => {
    fetchPromoHistory();
  }, [fetchPromoHistory]);

  // Handle promo code generation
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    setSuccess('');

    try {
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Add new promo code to history (simulate successful creation)
      const newPromo = {
        id: Date.now(),
        code,
        amount_off: amountOff ? parseInt(amountOff) : null,
        percent_off: percentOff ? parseInt(percentOff) : null,
        duration,
        created: new Date(),
        createdBy: auth.currentUser?.email || 'admin@servicegest.ro'
      };

      setHistory([newPromo, ...history]);
      setSuccess(i18n.promoCodeGenerated);
      setCode('');
      setAmountOff('');
      setPercentOff('');
      setDuration('once');
    } catch (error) {
      console.error('Error generating promo code:', error);
      setError(i18n.failedToGenerate);
    } finally {
      setSubmitting(false);
    }
  };

  // Format date for display
  const formatDate = (date) => {
    return date instanceof Date ? date.toLocaleString() : new Date(date).toLocaleString();
  };

  if (loading) {
    return (
      <div className="min-h-screen py-6 bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center items-center h-64">
            <div className="text-center">
              <svg className="animate-spin h-12 w-12 text-purple-600 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <p className="mt-4 text-lg text-slate-600">{i18n.loadingPromoCodes}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-6 bg-gradient-to-br from-slate-50 to-slate-100">
      <div className="mx-auto px-4 sm:px-6 lg:px-8">
        <form onSubmit={handleSubmit} className="bg-white shadow-lg rounded-xl p-8 border border-slate-200 mb-8 space-y-6">
          <div>
            <label className="block text-sm font-medium text-slate-700">{i18n.code}</label>
            <input 
              type="text" 
              value={code} 
              onChange={e => setCode(e.target.value)} 
              required 
              className="mt-1 block w-full py-2 px-3 border border-slate-300 rounded-lg shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-slate-700">{i18n.amountOff} ({i18n.ron})</label>
              <input 
                type="number" 
                value={amountOff} 
                onChange={e => setAmountOff(e.target.value)} 
                className="mt-1 block w-full py-2 px-3 border border-slate-300 rounded-lg shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                placeholder="e.g., 50"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700">{i18n.percentOff} (%)</label>
              <input 
                type="number" 
                value={percentOff} 
                onChange={e => setPercentOff(e.target.value)} 
                className="mt-1 block w-full py-2 px-3 border border-slate-300 rounded-lg shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                placeholder="e.g., 10"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700">{i18n.duration}</label>
            <select 
              value={duration} 
              onChange={e => setDuration(e.target.value)} 
              className="mt-1 block w-full py-2 px-3 border border-slate-300 bg-white rounded-lg shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
            >
              <option value="once">{i18n.once}</option>
              <option value="repeating">{i18n.repeating}</option>
              <option value="forever">{i18n.forever}</option>
            </select>
          </div>
          {error && <div className="p-3 rounded-md bg-red-50 text-red-700 text-sm border border-red-200">{error}</div>}
          {success && <div className="p-3 rounded-md bg-green-50 text-green-700 text-sm border border-green-200">{success}</div>}
          <button 
            type="submit" 
            disabled={submitting} 
            className="w-full bg-purple-600 text-white py-2.5 px-4 rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors duration-200 font-semibold shadow-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
          >
            {submitting ? i18n.generating : i18n.generatePromoCode}
          </button>
        </form>
        
        <h2 className="text-2xl font-bold text-slate-800 mb-6">{i18n.promoCodeHistory}</h2>
        <div className="bg-white shadow-lg rounded-xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">{i18n.code}</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">{i18n.amountOff}</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">{i18n.percentOff}</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">{i18n.duration}</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">{t('created')}</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">{t('by')}</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-200">
                {history.map((promo) => (
                  <tr key={promo.id} className="hover:bg-slate-50 transition-colors duration-150">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-purple-600">{promo.code}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-700">{promo.amount_off ? promo.amount_off + ` ${i18n.ron}` : '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-700">{promo.percent_off ? promo.percent_off + '%' : '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-700">{i18n[promo.duration]}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-xs text-slate-500">{formatDate(promo.created)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-xs text-slate-500">{promo.createdBy}</td>
                  </tr>
                ))}
                {history.length === 0 && !loading && (
                  <tr>
                    <td colSpan="6" className="px-6 py-10 text-center text-sm text-slate-500">
                      <svg className="mx-auto h-12 w-12 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                        <path vectorEffect="non-scaling-stroke" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 13h6m-3-3v6m-9 1V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                      </svg>
                      <p className="mt-2 font-semibold">{i18n.noPromoCodes}</p>
                      <p className="mt-1 text-xs text-slate-400">Generate a new promo code using the form above.</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PromoCodeGenerator;
